import React from 'react'
import { Language } from './data/types'
import CodeExample from './CodeExample'

interface WebviewDocsProps {
    language: Language
}

const WebviewDocs: React.FC<WebviewDocsProps> = ({ language }) => {
    return (
        <div className="docs-main-content">
            {/* Introduction */}
            <section id="webview-introduction">
                <div className="docs-header">
                    <h1 className="docs-title">
                        {language === 'pt' ? 'Documentação do Webview EmpreX' : 'EmpreX Webview Documentation'}
                    </h1>
                    <p className="docs-description">
                        {language === 'pt'
                            ? 'Interface web completa para solicitação de empréstimos que pode ser integrada em aplicativos de parceiros via WebView ou iFrame.'
                            : 'Complete web interface for loan requests that can be integrated into partner applications via WebView or iFrame.'
                        }
                    </p>
                </div>

                <div className="auth-section">
                    <h2 className="docs-category-title">
                        {language === 'pt' ? 'Visão Geral' : 'Overview'}
                    </h2>
                    <div className="auth-method-content">
                        <p>
                            {language === 'pt'
                                ? 'O Webview EmpreX oferece uma experiência completa de solicitação de empréstimo para usuários finais, incluindo:'
                                : 'The EmpreX Webview offers a complete loan request experience for end users, including:'
                            }
                        </p>
                        <ul>
                            <li><strong>{language === 'pt' ? 'Oferta de empréstimo' : 'Loan offer'}</strong> - {language === 'pt' ? 'Simulação e apresentação de ofertas' : 'Simulation and presentation of offers'}</li>
                            <li><strong>{language === 'pt' ? 'Dados pessoais' : 'Personal data'}</strong> - {language === 'pt' ? 'Formulário completo com validação' : 'Complete form with validation'}</li>
                            <li><strong>{language === 'pt' ? 'KYC próprio' : 'Built-in KYC'}</strong> - {language === 'pt' ? 'Captura de selfie e documentos' : 'Selfie and document capture'}</li>
                            <li><strong>{language === 'pt' ? 'Assinatura digital' : 'Digital signature'}</strong> - {language === 'pt' ? 'Revisão e assinatura de contrato' : 'Contract review and signature'}</li>
                            <li><strong>{language === 'pt' ? 'Integração com parceiros' : 'Partner integration'}</strong> - {language === 'pt' ? 'Callbacks e postMessage' : 'Callbacks and postMessage'}</li>
                        </ul>

                        <div style={{ marginTop: '24px', padding: '20px', backgroundColor: '#f0f9ff', borderLeft: '4px solid #10b981', borderRadius: '8px' }}>
                            <h4 style={{ fontSize: '16px', fontWeight: 700, color: '#059669', marginBottom: '12px' }}>
                                ✅ {language === 'pt' ? 'Vantagem: Apenas Admin API necessária' : 'Advantage: Only Admin API needed'}
                            </h4>
                            <p style={{ margin: 0, fontSize: '14px', color: '#4a5568', lineHeight: '1.6' }}>
                                {language === 'pt'
                                    ? 'Ao usar o Webview EmpreX, você não precisa implementar a Client API. Basta implementar apenas a Admin API para gerar tokens JWT, processar cobranças e acessar relatórios. Todo o fluxo do usuário final é gerenciado pelo nosso front-end pronto.'
                                    : 'When using EmpreX Webview, you don\'t need to implement the Client API. Just implement the Admin API to generate JWT tokens, process payments and access reports. The entire end-user flow is managed by our ready-made front-end.'
                                }
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Admin API Section */}
            <section id="webview-admin-api" className="docs-category">
                <div className="docs-category-header">
                    <h2 className="docs-category-title">
                        {language === 'pt' ? 'API Administrativa (Necessária)' : 'Admin API (Required)'}
                    </h2>
                    <p className="docs-category-description">
                        {language === 'pt'
                            ? 'Para usar o Webview, você precisa implementar apenas os endpoints da Admin API.'
                            : 'To use the Webview, you only need to implement Admin API endpoints.'
                        }
                    </p>
                </div>

                <div className="auth-section">
                    <div className="auth-method">
                        <h3 className="auth-method-title">
                            {language === 'pt' ? '1. Gerar Token JWT' : '1. Generate JWT Token'}
                            <span style={{ marginLeft: '8px', fontSize: '12px', padding: '4px 8px', backgroundColor: '#10b981', color: 'white', borderRadius: '4px', fontWeight: 700 }}>POST</span>
                        </h3>
                        <div className="auth-method-content">
                            <p>
                                {language === 'pt'
                                    ? 'Endpoint para gerar token JWT que permite aos seus usuários acessarem o Webview EmpreX.'
                                    : 'Endpoint to generate JWT token that allows your users to access EmpreX Webview.'
                                }
                            </p>
                            <p style={{ fontSize: '14px', color: '#6a737d', marginBottom: '16px' }}>
                                <strong>{language === 'pt' ? 'Caminho:' : 'Path:'}</strong> <code>/api/admin/get-token</code>
                            </p>
                            <CodeExample
                                code={`curl -X POST https://api.emprex.com.br/api/admin/get-token \\
  -H "Authorization: Bearer YOUR_ADMIN_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"userId": "user-123", "externalId": "user-456"}'`}
                                language="bash"
                            />
                        </div>
                    </div>

                    <div className="auth-method">
                        <h3 className="auth-method-title">
                            {language === 'pt' ? '2. Processar Cobrança (Retenção Percentual)' : '2. Process Collection (Percentage Retention)'}
                            <span style={{ marginLeft: '8px', fontSize: '12px', padding: '4px 8px', backgroundColor: '#10b981', color: 'white', borderRadius: '4px', fontWeight: 700 }}>POST</span>
                        </h3>
                        <div className="auth-method-content">
                            <p>
                                {language === 'pt'
                                    ? 'Use este endpoint quando um percentual fixo dos valores repassados ao usuário for automaticamente retido para pagamento do empréstimo.'
                                    : 'Use this endpoint when a fixed percentage of amounts transferred to the user is automatically retained for loan payment.'
                                }
                            </p>
                            <p style={{ fontSize: '14px', color: '#6a737d', marginBottom: '16px' }}>
                                <strong>{language === 'pt' ? 'Caminho:' : 'Path:'}</strong> <code>/api/admin/collection</code>
                            </p>
                            <CodeExample
                                code={`curl -X POST https://api.emprex.com.br/api/admin/collection \\
  -H "Authorization: Bearer YOUR_ADMIN_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"loanId": "loan-123", "amount": 275, "paymentDate": "2025-11-01"}'`}
                                language="bash"
                            />
                        </div>
                    </div>

                    <div className="auth-method">
                        <h3 className="auth-method-title">
                            {language === 'pt' ? '3. Gerar Fatura (Parcelamento no Billing)' : '3. Generate Invoice (Billing Installment)'}
                            <span style={{ marginLeft: '8px', fontSize: '12px', padding: '4px 8px', backgroundColor: '#10b981', color: 'white', borderRadius: '4px', fontWeight: 700 }}>POST</span>
                        </h3>
                        <div className="auth-method-content">
                            <p>
                                {language === 'pt'
                                    ? 'Use este endpoint para adicionar as parcelas do empréstimo à cobrança recorrente da plataforma (ex: fatura semanal ou mensal).'
                                    : 'Use this endpoint to add loan installments to the platform\'s recurring billing (e.g., weekly or monthly invoice).'
                                }
                            </p>
                            <p style={{ fontSize: '14px', color: '#6a737d', marginBottom: '16px' }}>
                                <strong>{language === 'pt' ? 'Caminho:' : 'Path:'}</strong> <code>/api/admin/events/create-invoice</code>
                            </p>
                            <CodeExample
                                code={`curl -X POST https://api.emprex.com.br/api/admin/events/create-invoice \\
  -H "Authorization: Bearer YOUR_ADMIN_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"loanId": "loan-123", "installment": 1, "amount": 275, "dueDate": "2025-11-01"}'`}
                                language="bash"
                            />
                        </div>
                    </div>

                    <div className="auth-method">
                        <h3 className="auth-method-title">
                            {language === 'pt' ? '4. Obter Detalhes do Empréstimo' : '4. Get Loan Details'}
                            <span style={{ marginLeft: '8px', fontSize: '12px', padding: '4px 8px', backgroundColor: '#059669', color: 'white', borderRadius: '4px', fontWeight: 700 }}>GET</span>
                        </h3>
                        <div className="auth-method-content">
                            <p>
                                {language === 'pt'
                                    ? 'Recupera informações detalhadas de um empréstimo (relatório administrativo).'
                                    : 'Retrieve detailed loan information (administrative report).'
                                }
                            </p>
                            <p style={{ fontSize: '14px', color: '#6a737d', marginBottom: '16px' }}>
                                <strong>{language === 'pt' ? 'Caminho:' : 'Path:'}</strong> <code>/api/admin/loan-details</code>
                            </p>
                            <CodeExample
                                code={`curl -X GET "https://api.emprex.com.br/api/admin/loan-details?loanId=loan-123" \\
  -H "Authorization: Bearer YOUR_ADMIN_TOKEN"`}
                                language="bash"
                            />
                        </div>
                    </div>

                    <div className="auth-method">
                        <h3 className="auth-method-title">
                            {language === 'pt' ? '5. Obter Detalhes de Pagamento' : '5. Get Payment Details'}
                            <span style={{ marginLeft: '8px', fontSize: '12px', padding: '4px 8px', backgroundColor: '#059669', color: 'white', borderRadius: '4px', fontWeight: 700 }}>GET</span>
                        </h3>
                        <div className="auth-method-content">
                            <p>
                                {language === 'pt'
                                    ? 'Recupera histórico de pagamentos e detalhes (relatório administrativo).'
                                    : 'Retrieve payment history and details (administrative report).'
                                }
                            </p>
                            <p style={{ fontSize: '14px', color: '#6a737d', marginBottom: '16px' }}>
                                <strong>{language === 'pt' ? 'Caminho:' : 'Path:'}</strong> <code>/api/admin/payment-details</code>
                            </p>
                            <CodeExample
                                code={`curl -X GET "https://api.emprex.com.br/api/admin/payment-details?loanId=loan-123" \\
  -H "Authorization: Bearer YOUR_ADMIN_TOKEN"`}
                                language="bash"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Integration */}
            <section id="webview-integration" className="docs-category">
                <div className="docs-category-header">
                    <h2 className="docs-category-title">
                        {language === 'pt' ? 'Integração' : 'Integration'}
                    </h2>
                    <p className="docs-category-description">
                        {language === 'pt'
                            ? 'Como integrar o Webview no seu aplicativo ou site.'
                            : 'How to integrate the Webview into your application or website.'
                        }
                    </p>
                </div>

                <div className="auth-section">
                    <div className="auth-method">
                        <h3 className="auth-method-title">
                            {language === 'pt' ? 'WebView (Mobile)' : 'WebView (Mobile)'}
                        </h3>
                        <div className="auth-method-content">
                            <p>
                                {language === 'pt'
                                    ? 'Para integrar em aplicativos mobile nativos, use um componente WebView:'
                                    : 'To integrate into native mobile apps, use a WebView component:'
                                }
                            </p>
                            <CodeExample
                                code={`// 1. Obter token JWT via API
const token = await getPartnerToken()

// 2. Construir URL com parâmetros
const url = \`https://webview.emprex.com.br?token=\${token}&amount=500&callback=app://loan-complete\`

// 3. Carregar no WebView
webview.loadUrl(url)

// Android
WebView webView = findViewById(R.id.webview);
webView.loadUrl(url);

// iOS
let webView = WKWebView()
webView.load(URLRequest(url: URL(string: url)!))`}
                                language="javascript"
                            />
                        </div>
                    </div>

                    <div className="auth-method">
                        <h3 className="auth-method-title">
                            {language === 'pt' ? 'iFrame (Web)' : 'iFrame (Web)'}
                        </h3>
                        <div className="auth-method-content">
                            <p>
                                {language === 'pt'
                                    ? 'Para integrar em sites, use um iFrame:'
                                    : 'To integrate into websites, use an iFrame:'
                                }
                            </p>
                            <CodeExample
                                code={`<iframe
  src="https://webview.emprex.com.br?token=JWT_TOKEN&amount=500"
  width="100%"
  height="600"
  frameborder="0"
  sandbox="allow-scripts allow-same-origin allow-forms allow-modals allow-popups"
  allow="camera;microphone"
/>`}
                                language="html"
                            />
                            <p style={{ marginTop: '16px' }}>
                                <strong>{language === 'pt' ? 'Nota:' : 'Note:'}</strong> {language === 'pt'
                                    ? 'O sandbox permite scripts, formulários e modais. Adicione allow="camera;microphone" para permitir captura de selfie.'
                                    : 'The sandbox allows scripts, forms, and modals. Add allow="camera;microphone" to enable selfie capture.'
                                }
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Parameters */}
            <section id="webview-parameters" className="docs-category">
                <div className="docs-category-header">
                    <h2 className="docs-category-title">
                        {language === 'pt' ? 'Parâmetros de URL' : 'URL Parameters'}
                    </h2>
                    <p className="docs-category-description">
                        {language === 'pt'
                            ? 'Parâmetros disponíveis para configuração do Webview.'
                            : 'Available parameters for Webview configuration.'
                        }
                    </p>
                </div>

                <div className="auth-section">
                    <table className="parameters-table">
                        <thead>
                            <tr>
                                <th>{language === 'pt' ? 'Parâmetro' : 'Parameter'}</th>
                                <th>{language === 'pt' ? 'Tipo' : 'Type'}</th>
                                <th>{language === 'pt' ? 'Obrigatório' : 'Required'}</th>
                                <th>{language === 'pt' ? 'Descrição' : 'Description'}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>token</code></td>
                                <td>string</td>
                                <td className="param-required">{language === 'pt' ? 'Sim' : 'Yes'}</td>
                                <td>{language === 'pt' ? 'JWT token obtido via' : 'JWT token obtained via'} <code>/api/admin/get-token</code></td>
                            </tr>
                            <tr>
                                <td><code>amount</code></td>
                                <td>number</td>
                                <td className="param-optional">{language === 'pt' ? 'Não' : 'No'}</td>
                                <td>{language === 'pt' ? 'Valor inicial do empréstimo em reais (ex: 1000)' : 'Initial loan amount in reais (e.g., 1000)'}</td>
                            </tr>
                            <tr>
                                <td><code>callback</code></td>
                                <td>string</td>
                                <td className="param-optional">{language === 'pt' ? 'Não' : 'No'}</td>
                                <td>{language === 'pt' ? 'URL de retorno após conclusão (ex: app://loan-complete)' : 'Return URL after completion (e.g., app://loan-complete)'}</td>
                            </tr>
                            <tr>
                                <td><code>name</code></td>
                                <td>string</td>
                                <td className="param-optional">{language === 'pt' ? 'Não' : 'No'}</td>
                                <td>{language === 'pt' ? 'Nome completo do cliente (pré-preenche formulário)' : 'Client full name (pre-fills form)'}</td>
                            </tr>
                            <tr>
                                <td><code>cpf</code></td>
                                <td>string</td>
                                <td className="param-optional">{language === 'pt' ? 'Não' : 'No'}</td>
                                <td>{language === 'pt' ? 'CPF do cliente (pré-preenche formulário)' : 'Client CPF (pre-fills form)'}</td>
                            </tr>
                            <tr>
                                <td><code>email</code></td>
                                <td>string</td>
                                <td className="param-optional">{language === 'pt' ? 'Não' : 'No'}</td>
                                <td>{language === 'pt' ? 'E-mail do cliente (pré-preenche formulário)' : 'Client email (pre-fills form)'}</td>
                            </tr>
                            <tr>
                                <td><code>phone</code></td>
                                <td>string</td>
                                <td className="param-optional">{language === 'pt' ? 'Não' : 'No'}</td>
                                <td>{language === 'pt' ? 'Telefone do cliente (pré-preenche formulário)' : 'Client phone (pre-fills form)'}</td>
                            </tr>
                        </tbody>
                    </table>

                    <div style={{ marginTop: '24px' }}>
                        <h4 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px' }}>
                            {language === 'pt' ? 'Exemplo completo:' : 'Complete example:'}
                        </h4>
                        <CodeExample
                            code={`https://webview.emprex.com.br?token=eyJhbGc...&amount=1000&callback=app://success&name=João+Silva&cpf=12345678900`}
                            language="bash"
                        />
                    </div>
                </div>
            </section>

            {/* Callbacks */}
            <section id="webview-callbacks" className="docs-category">
                <div className="docs-category-header">
                    <h2 className="docs-category-title">
                        {language === 'pt' ? 'Callbacks e Eventos' : 'Callbacks and Events'}
                    </h2>
                    <p className="docs-category-description">
                        {language === 'pt'
                            ? 'Como receber notificações sobre eventos do Webview.'
                            : 'How to receive notifications about Webview events.'
                        }
                    </p>
                </div>

                <div className="auth-section">
                    <div className="auth-method">
                        <h3 className="auth-method-title">
                            {language === 'pt' ? 'Via postMessage (iFrame)' : 'Via postMessage (iFrame)'}
                        </h3>
                        <div className="auth-method-content">
                            <p>
                                {language === 'pt'
                                    ? 'Quando integrado via iFrame, o Webview envia eventos via postMessage:'
                                    : 'When integrated via iFrame, the Webview sends events via postMessage:'
                                }
                            </p>
                            <CodeExample
                                code={`window.addEventListener('message', (event) => {
  // Verificar origem por segurança
  if (event.origin !== 'https://webview.emprex.com.br') return

  const { event: eventType, status, data } = event.data

  switch(eventType) {
    case 'loan.started':
      console.log('Usuário iniciou solicitação')
      break

    case 'loan.offer':
      console.log('Oferta apresentada:', data.amount, data.installments)
      break

    case 'loan.personal-data':
      console.log('Dados pessoais enviados')
      break

    case 'loan.kyc':
      console.log('KYC iniciado')
      break

    case 'loan.signed':
      console.log('Contrato assinado, aguardando aprovação')
      break

    case 'loan.completed':
      console.log('Empréstimo aprovado!', data.loanId)
      // Redirecionar usuário ou mostrar sucesso
      break

    case 'loan.failed':
      console.error('Empréstimo rejeitado:', data.reason)
      break

    case 'loan.cancelled':
      console.log('Usuário cancelou o processo')
      break
  }
})`}
                                language="javascript"
                            />
                        </div>
                    </div>

                    <div className="auth-method">
                        <h3 className="auth-method-title">
                            {language === 'pt' ? 'Via URL (WebView)' : 'Via URL (WebView)'}
                        </h3>
                        <div className="auth-method-content">
                            <p>
                                {language === 'pt'
                                    ? 'Quando integrado em app nativo, o callback é chamado com parâmetros na URL:'
                                    : 'When integrated in a native app, the callback is called with parameters in the URL:'
                                }
                            </p>
                            <CodeExample
                                code={`// URL de sucesso
app://loan-complete?status=success&loanId=loan_123456&amount=1000

// URL de falha
app://loan-complete?status=failed&reason=rejected_by_analysis

// URL de cancelamento
app://loan-complete?status=cancelled`}
                                language="bash"
                            />
                            <p style={{ marginTop: '16px' }}>
                                <strong>{language === 'pt' ? 'Exemplo Android:' : 'Android example:'}</strong>
                            </p>
                            <CodeExample
                                code={`// AndroidManifest.xml
<activity android:name=".LoanResultActivity">
    <intent-filter>
        <action android:name="android.intent.action.VIEW" />
        <category android:name="android.intent.category.DEFAULT" />
        <category android:name="android.intent.category.BROWSABLE" />
        <data android:scheme="app" android:host="loan-complete" />
    </intent-filter>
</activity>

// LoanResultActivity.java
Uri uri = getIntent().getData();
String status = uri.getQueryParameter("status");
String loanId = uri.getQueryParameter("loanId");`}
                                language="java"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* User Flow */}
            <section id="webview-flow" className="docs-category">
                <div className="docs-category-header">
                    <h2 className="docs-category-title">
                        {language === 'pt' ? 'Fluxo do Usuário' : 'User Flow'}
                    </h2>
                    <p className="docs-category-description">
                        {language === 'pt'
                            ? 'Etapas que o usuário percorre durante o processo de solicitação de empréstimo.'
                            : 'Steps that the user goes through during the loan request process.'
                        }
                    </p>
                </div>

                <div className="auth-section">
                    <div style={{
                        display: 'grid',
                        gap: '20px',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))'
                    }}>
                        {[
                            {
                                step: '1',
                                icon: '💰',
                                title: language === 'pt' ? 'Oferta' : 'Offer',
                                description: language === 'pt'
                                    ? 'Usuário define o valor desejado e visualiza simulação com juros, parcelas e total a pagar.'
                                    : 'User defines desired amount and views simulation with interest, installments, and total to pay.'
                            },
                            {
                                step: '2',
                                icon: '📝',
                                title: language === 'pt' ? 'Dados Pessoais' : 'Personal Data',
                                description: language === 'pt'
                                    ? 'Preenche formulário com CPF, nome, data de nascimento, nome da mãe e telefone.'
                                    : 'Fills form with CPF, name, date of birth, mother\'s name, and phone.'
                            },
                            {
                                step: '3',
                                icon: '🏠',
                                title: language === 'pt' ? 'Endereço' : 'Address',
                                description: language === 'pt'
                                    ? 'Informa endereço completo: CEP, rua, número, bairro, cidade e estado.'
                                    : 'Provides complete address: ZIP code, street, number, neighborhood, city, and state.'
                            },
                            {
                                step: '4',
                                icon: '💳',
                                title: language === 'pt' ? 'Chave PIX' : 'PIX Key',
                                description: language === 'pt'
                                    ? 'Valida chave PIX (CPF, telefone, e-mail ou aleatória) para recebimento do empréstimo.'
                                    : 'Validates PIX key (CPF, phone, email, or random) to receive the loan.'
                            },
                            {
                                step: '5',
                                icon: '🤳',
                                title: language === 'pt' ? 'Selfie' : 'Selfie',
                                description: language === 'pt'
                                    ? 'Captura foto do rosto para validação biométrica e análise de identidade (KYC).'
                                    : 'Captures face photo for biometric validation and identity analysis (KYC).'
                            },
                            {
                                step: '6',
                                icon: '📄',
                                title: language === 'pt' ? 'Documentos' : 'Documents',
                                description: language === 'pt'
                                    ? 'Upload de frente e verso do RG ou CNH para validação de identidade (KYC).'
                                    : 'Upload front and back of ID or driver\'s license for identity validation (KYC).'
                            },
                            {
                                step: '7',
                                icon: '✍️',
                                title: language === 'pt' ? 'Assinatura' : 'Signature',
                                description: language === 'pt'
                                    ? 'Revisa termos do contrato (CCB) e assina digitalmente para finalizar.'
                                    : 'Reviews contract terms (CCB) and signs digitally to complete.'
                            },
                            {
                                step: '8',
                                icon: '✅',
                                title: language === 'pt' ? 'Aprovação' : 'Approval',
                                description: language === 'pt'
                                    ? 'Aguarda análise e aprovação. Recebe notificação com resultado do empréstimo.'
                                    : 'Awaits analysis and approval. Receives notification with loan result.'
                            }
                        ].map((flow) => (
                            <div key={flow.step} style={{
                                backgroundColor: '#fff',
                                border: '1px solid #e1e4e8',
                                borderRadius: '12px',
                                padding: '24px',
                                position: 'relative',
                                transition: 'all 0.2s ease'
                            }}>
                                <div style={{
                                    position: 'absolute',
                                    top: '-12px',
                                    left: '16px',
                                    backgroundColor: '#10b981',
                                    color: 'white',
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 700,
                                    fontSize: '14px',
                                    boxShadow: '0 4px 8px rgba(16, 185, 129, 0.3)'
                                }}>
                                    {flow.step}
                                </div>
                                <div style={{ fontSize: '32px', marginBottom: '12px' }}>
                                    {flow.icon}
                                </div>
                                <h4 style={{
                                    fontSize: '18px',
                                    fontWeight: 600,
                                    marginBottom: '8px',
                                    color: '#24292e'
                                }}>
                                    {flow.title}
                                </h4>
                                <p style={{
                                    fontSize: '14px',
                                    color: '#586069',
                                    lineHeight: '1.6',
                                    margin: 0
                                }}>
                                    {flow.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Customization */}
            <section id="webview-customization" className="docs-category">
                <div className="docs-category-header">
                    <h2 className="docs-category-title">
                        {language === 'pt' ? 'Customização' : 'Customization'}
                    </h2>
                    <p className="docs-category-description">
                        {language === 'pt'
                            ? 'O Webview carrega configurações de personalização automaticamente via endpoint de configuração.'
                            : 'The Webview loads customization settings automatically via configuration endpoint.'
                        }
                    </p>
                </div>

                <div className="auth-section">
                    <div className="auth-method">
                        <h3 className="auth-method-title">
                            {language === 'pt' ? 'Endpoint de Configuração' : 'Configuration Endpoint'}
                        </h3>
                        <div className="auth-method-content">
                            <p>
                                {language === 'pt'
                                    ? 'O Webview busca configurações personalizadas automaticamente ao iniciar:'
                                    : 'The Webview fetches custom settings automatically on startup:'
                                }
                            </p>
                            <CodeExample
                                code={`GET /api/client/webview-config

Authorization: Bearer {token}

// Resposta
{
  "success": true,
  "data": {
    "partnerName": "Minha Empresa",
    "logoUrl": "https://example.com/logo.png",
    "primaryColor": "#10b981",
    "secondaryColor": "#059669",
    "customCSS": ".button { border-radius: 8px; }"
  }
}`}
                                language="javascript"
                            />
                        </div>
                    </div>

                    <div className="auth-method">
                        <h3 className="auth-method-title">
                            {language === 'pt' ? 'Opções Disponíveis' : 'Available Options'}
                        </h3>
                        <div className="auth-method-content">
                            <ul>
                                <li><strong>partnerName</strong> - {language === 'pt' ? 'Nome do parceiro exibido no cabeçalho' : 'Partner name displayed in header'}</li>
                                <li><strong>logoUrl</strong> - {language === 'pt' ? 'URL do logo do parceiro' : 'Partner logo URL'}</li>
                                <li><strong>primaryColor</strong> - {language === 'pt' ? 'Cor primária (botões, destaques)' : 'Primary color (buttons, highlights)'}</li>
                                <li><strong>secondaryColor</strong> - {language === 'pt' ? 'Cor secundária (elementos de suporte)' : 'Secondary color (supporting elements)'}</li>
                                <li><strong>customCSS</strong> - {language === 'pt' ? 'CSS customizado injetado na página' : 'Custom CSS injected into the page'}</li>
                            </ul>
                            <p style={{ marginTop: '16px' }}>
                                <strong>{language === 'pt' ? 'Nota:' : 'Note:'}</strong> {language === 'pt'
                                    ? 'As cores são aplicadas automaticamente usando CSS custom properties (--primary-color, --secondary-color).'
                                    : 'Colors are automatically applied using CSS custom properties (--primary-color, --secondary-color).'
                                }
                            </p>
                        </div>
                    </div>

                    <div className="auth-method">
                        <h3 className="auth-method-title">
                            {language === 'pt' ? 'Como Configurar' : 'How to Configure'}
                        </h3>
                        <div className="auth-method-content">
                            <p>
                                {language === 'pt'
                                    ? 'Entre em contato com a equipe EmpreX para configurar as opções de customização do seu parceiro. As configurações são gerenciadas no backend e aplicadas automaticamente.'
                                    : 'Contact the EmpreX team to configure your partner customization options. Settings are managed on the backend and applied automatically.'
                                }
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Security */}
            <section id="webview-security" className="docs-category">
                <div className="docs-category-header">
                    <h2 className="docs-category-title">
                        {language === 'pt' ? 'Segurança' : 'Security'}
                    </h2>
                    <p className="docs-category-description">
                        {language === 'pt'
                            ? 'Práticas de segurança e considerações importantes.'
                            : 'Security practices and important considerations.'
                        }
                    </p>
                </div>

                <div className="auth-section">
                    <div className="auth-method-content">
                        <h4 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px' }}>
                            {language === 'pt' ? '🔒 Autenticação' : '🔒 Authentication'}
                        </h4>
                        <ul>
                            <li><strong>JWT Token</strong> - {language === 'pt' ? 'Todos os acessos exigem token válido' : 'All access requires valid token'}</li>
                            <li><strong>{language === 'pt' ? 'Expiração' : 'Expiration'}</strong> - {language === 'pt' ? 'Tokens expiram em 1 hora' : 'Tokens expire in 1 hour'}</li>
                            <li><strong>{language === 'pt' ? 'Validação em tempo real' : 'Real-time validation'}</strong> - {language === 'pt' ? 'Cada requisição é validada' : 'Each request is validated'}</li>
                        </ul>

                        <h4 style={{ fontSize: '16px', fontWeight: 600, marginTop: '24px', marginBottom: '12px' }}>
                            {language === 'pt' ? '🔐 Dados Sensíveis' : '🔐 Sensitive Data'}
                        </h4>
                        <ul>
                            <li><strong>Session Storage</strong> - {language === 'pt' ? 'Dados temporários apenas durante a sessão' : 'Temporary data only during session'}</li>
                            <li><strong>HTTPS</strong> - {language === 'pt' ? 'Obrigatório em produção' : 'Mandatory in production'}</li>
                            <li><strong>{language === 'pt' ? 'Sem persistência' : 'No persistence'}</strong> - {language === 'pt' ? 'Dados não são salvos no navegador' : 'Data is not saved in browser'}</li>
                        </ul>

                        <h4 style={{ fontSize: '16px', fontWeight: 600, marginTop: '24px', marginBottom: '12px' }}>
                            {language === 'pt' ? '📁 Upload de Arquivos' : '📁 File Upload'}
                        </h4>
                        <ul>
                            <li><strong>{language === 'pt' ? 'Limite de tamanho' : 'Size limit'}</strong> - {language === 'pt' ? 'Máximo de 10MB por arquivo' : 'Maximum 10MB per file'}</li>
                            <li><strong>{language === 'pt' ? 'Formatos aceitos' : 'Accepted formats'}</strong> - JPEG, PNG, PDF</li>
                            <li><strong>{language === 'pt' ? 'Validação' : 'Validation'}</strong> - {language === 'pt' ? 'Verificação de tipo e conteúdo' : 'Type and content verification'}</li>
                        </ul>

                        <h4 style={{ fontSize: '16px', fontWeight: 600, marginTop: '24px', marginBottom: '12px' }}>
                            {language === 'pt' ? '🌐 CORS e CSP' : '🌐 CORS and CSP'}
                        </h4>
                        <ul>
                            <li><strong>CORS</strong> - {language === 'pt' ? 'Configurado para domínios parceiros autorizados' : 'Configured for authorized partner domains'}</li>
                            <li><strong>CSP</strong> - {language === 'pt' ? 'Content Security Policy configurado' : 'Content Security Policy configured'}</li>
                            <li><strong>Sandbox</strong> - {language === 'pt' ? 'Recomendado para iFrames' : 'Recommended for iFrames'}</li>
                        </ul>

                        <div style={{
                            marginTop: '24px',
                            padding: '16px',
                            backgroundColor: '#ecfdf5',
                            border: '1px solid #a7f3d0',
                            borderRadius: '8px'
                        }}>
                            <p style={{ margin: 0, color: '#047857', fontWeight: 500 }}>
                                💡 <strong>{language === 'pt' ? 'Dica:' : 'Tip:'}</strong> {language === 'pt'
                                    ? 'Sempre valide a origem dos eventos postMessage para evitar ataques XSS.'
                                    : 'Always validate the origin of postMessage events to prevent XSS attacks.'
                                }
                            </p>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    )
}

export default WebviewDocs
